import {
  isNotificationKindResendable,
  NOTIFICATION_KIND_LABELS,
  type NotificationDeliveryItem,
  notificationSkipReasonLabel,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { NotificationStatusBadge } from '@/entities/notification';
import { formatDateTime } from '@/shared/lib';
import { Button, cn, tableStyles } from '@/shared/ui';

interface NotificationDeliveryRowProps {
  /** 계약 타입을 그대로 받는다. */
  readonly delivery: NotificationDeliveryItem;
  readonly onResend: (delivery: NotificationDeliveryItem) => void;
}

const MUTED_CLASS = 'text-muted-foreground';
const SUB_TEXT_CLASS = 'text-xs text-muted-foreground';
const LINK_CLASS =
  'font-medium whitespace-nowrap underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground focus-visible:decoration-foreground focus-visible:outline-none';
/** 사유 칸에 보이는 글자 수. 잘리면 전체 사유는 title(마우스)과 시각적으로 숨긴 글(화면 낭독기)로 준다. */
const REASON_PREVIEW_LENGTH = 80;
/** 인증 메일은 토큰 원문을 저장하지 않아 다시 만들 수 없다(N6). 고객이 화면에서 다시 요청한다. */
const AUTH_RESEND_HINT = '고객이 화면에서 다시 요청합니다';
/** 참조 링크 — 견적은 견적 상세, 주문은 주문 상세(알림 메일의 관리자 링크와 같은 경로). */
const REFERENCE_PATHS = { QUOTE_REQUEST: '/quotes', ORDER: '/orders' } as const;
/** 대상이 지워져 번호가 없을 때 링크 글자의 머리. */
const REFERENCE_NAMES = { QUOTE_REQUEST: '견적', ORDER: '주문' } as const;

const shortId = (id: string): string => id.slice(0, 8);

const previewOf = (text: string): string =>
  text.length > REASON_PREVIEW_LENGTH ? `${text.slice(0, REASON_PREVIEW_LENGTH)}…` : text;

/** 건너뜀은 사유 코드의 한글 라벨, 실패는 마지막 오류(SECRET_UNAVAILABLE 같은 코드는 한글 라벨, 그 밖에는 가린 원문). */
const reasonOf = (delivery: NotificationDeliveryItem): string | null => {
  if (delivery.status === 'SKIPPED' && delivery.skipReason !== null)
    return notificationSkipReasonLabel(delivery.skipReason);
  if (delivery.status === 'FAILED' && delivery.lastError !== null)
    return notificationSkipReasonLabel(delivery.lastError);
  return null;
};

/**
 * 알림 발송 이력의 한 행(알림 설계 §6-1). 발송 일시(대기면 만든 시각과 "대기 중")·종류(재발송 건 표시)·수신 이메일(탈퇴면
 * "탈퇴 회원")·참조(견적·주문 번호 링크, 인증은 회원 id 축약)·결과 배지·사유·처리 칸으로 이뤄진다. 처리 칸 규칙: 성공은
 * "다시 보내기", 실패·건너뜀은 "재발송" 버튼이다. 대기 중이면 버튼이 없다. 인증 메일은 버튼 대신 고객 재요청 안내를 둔다.
 */
export const NotificationDeliveryRow = ({
  delivery,
  onResend,
}: NotificationDeliveryRowProps): ReactElement => {
  const { reference } = delivery;
  const referenceType = reference.type;
  const reason = reasonOf(delivery);

  return (
    <tr className={tableStyles.row}>
      <td className={cn(tableStyles.td, 'whitespace-nowrap')}>
        {delivery.finishedAt !== null ? (
          formatDateTime(delivery.finishedAt)
        ) : (
          <span className="flex flex-col">
            <span>{formatDateTime(delivery.createdAt)}</span>
            <span className={SUB_TEXT_CLASS}>대기 중</span>
          </span>
        )}
      </td>
      <td className={cn(tableStyles.td, 'min-w-36')}>
        <span className="flex flex-col">
          <span>{NOTIFICATION_KIND_LABELS[delivery.kind]}</span>
          {delivery.resendOfId !== null ? <span className={SUB_TEXT_CLASS}>재발송 건</span> : null}
        </span>
      </td>
      <td className={cn(tableStyles.td, 'min-w-48 break-all')}>
        {delivery.recipientRedacted ? (
          <span className={MUTED_CLASS}>탈퇴 회원</span>
        ) : (
          (delivery.recipientEmail ?? <span className={MUTED_CLASS}>—</span>)
        )}
      </td>
      <td className={tableStyles.td}>
        {referenceType === 'USER' ? (
          <span className={cn(MUTED_CLASS, 'whitespace-nowrap')} title={reference.id}>
            회원 {shortId(reference.id)}
          </span>
        ) : (
          <Link to={`${REFERENCE_PATHS[referenceType]}/${reference.id}`} className={LINK_CLASS}>
            {reference.label ?? `${REFERENCE_NAMES[referenceType]} ${shortId(reference.id)}`}
          </Link>
        )}
      </td>
      <td className={tableStyles.td}>
        <NotificationStatusBadge status={delivery.status} />
      </td>
      <td className={cn(tableStyles.td, 'min-w-48')}>
        {reason === null ? (
          <span className={MUTED_CLASS}>—</span>
        ) : reason.length > REASON_PREVIEW_LENGTH ? (
          <>
            <span aria-hidden="true" title={reason}>
              {previewOf(reason)}
            </span>
            <span className="sr-only">{reason}</span>
          </>
        ) : (
          <span>{reason}</span>
        )}
      </td>
      <td className={cn(tableStyles.td, 'text-right whitespace-nowrap')}>
        {!isNotificationKindResendable(delivery.kind) ? (
          <span className={SUB_TEXT_CLASS} title={AUTH_RESEND_HINT}>
            고객 재요청
          </span>
        ) : delivery.status === 'QUEUED' ? (
          <span className={MUTED_CLASS}>—</span>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => onResend(delivery)}>
            {delivery.status === 'SENT' ? '다시 보내기' : '재발송'}
          </Button>
        )}
      </td>
    </tr>
  );
};
